import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Check, Home, Building2, Landmark } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { AdaptiveSheet, Button, ErrorBanner, LoadingSpinner } from '../common';
import { InsuranceChecklist, StakeholderType } from '../../types';
import { useChecklistStore } from '../../store/checklistStore';
import { useAuthStore } from '../../store/authStore';
import { groupModelsByHazard, modelHazard, type ModelHazard } from '../../utils/modelGroups';

interface ClaimSheetProps {
  visible: boolean;
  initialStakeholderType?: StakeholderType;
  initialChecklistId?: string;
  // `serviceAreaId` filters the insurer list; undefined means no location scoping.
  serviceAreaId?: string;
  // Pre-select the `isRecommended` model when nothing else applies. Only for
  // fresh claims — in edit mode an empty selection means the user already
  // chose the Baseline, and auto-selecting would silently switch their model.
  autoSelectRecommended?: boolean;
  submitLabel?: string;
  title?: string;
  isSubmitting?: boolean;
  // Submit failure surfaced by the parent (the sheet stays open on error).
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (args: { stakeholderType: StakeholderType; insurerChecklistId?: string }) => void;
}

const STAKEHOLDER_OPTIONS: {
  value: StakeholderType;
  label: string;
  icon: typeof Home;
}[] = [
  { value: 'homeowner', label: 'Homeowner', icon: Home },
  { value: 'business', label: 'Business', icon: Building2 },
  { value: 'municipality', label: 'Municipality', icon: Landmark },
];

const DRAFT_HINT = 'Being checked against the published standard';

// One-line model summary. The hazard is named by the group header above the row.
function modelDescription(c: InsuranceChecklist): string {
  return `${c.items.length} items`;
}

export function ClaimSheet({
  visible,
  initialStakeholderType,
  initialChecklistId,
  serviceAreaId,
  autoSelectRecommended = false,
  submitLabel = 'Claim this structure',
  title = 'Claim this structure',
  isSubmitting = false,
  errorMessage = null,
  onClose,
  onSubmit,
}: ClaimSheetProps) {
  const { colors } = useTheme();
  const { fetchChecklistsFor } = useChecklistStore();
  const defaultInsurer = useAuthStore((s) => s.user?.defaultInsurer);

  const [stakeholderType, setStakeholderType] = useState<StakeholderType>(
    initialStakeholderType ?? 'homeowner'
  );
  const [selectedChecklistId, setSelectedChecklistId] = useState<string | undefined>(
    initialChecklistId
  );
  const [checklists, setChecklists] = useState<InsuranceChecklist[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);

  // The sheet stays mounted across opens, so the useState initializers above
  // only ever see the props from the FIRST render — on a cold load that is
  // before the claim snapshot lands, i.e. `undefined`, which draws the default
  // row as selected. Opening the sheet on a claim already assigned to another
  // model and pressing Save then wrote that default back over it, silently
  // losing the model. Re-seed from the props on each open (edge-triggered, so
  // a claim arriving mid-edit never clobbers what the user just picked).
  const wasVisible = useRef(false);
  useEffect(() => {
    if (visible && !wasVisible.current) {
      setStakeholderType(initialStakeholderType ?? 'homeowner');
      setSelectedChecklistId(initialChecklistId);
    }
    wasVisible.current = visible;
  }, [visible, initialStakeholderType, initialChecklistId]);

  // Reload the insurer list whenever the stakeholder type (or scope) changes.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setIsLoadingList(true);
    fetchChecklistsFor(stakeholderType, serviceAreaId)
      .then((list) => {
        if (cancelled) return;
        setChecklists(list);
        // Preserve an initial selection if it's still in the list; otherwise
        // pre-select the best model for this user, falling back to "REDI
        // Baseline" (undefined) when nothing applies.
        setSelectedChecklistId((prev) => {
          // Preserve an explicit initial selection. The default model is
          // rendered as its own row keyed on `undefined`, so an initial id
          // that points AT the default doc (e.g. an insurer link's
          // ?model=fairplan-homeowner) normalizes to undefined — otherwise
          // the sheet would show no row selected.
          if (prev) {
            const hit = list.find((c) => c.id === prev);
            if (hit) return hit.isDefault ? undefined : prev;
          }
          // Auto-select if the user has a defaultInsurer from an invite.
          if (defaultInsurer) {
            const match = list.find((c) => !c.isDefault && c.insurerName === defaultInsurer);
            if (match) return match.id;
          }
          // Otherwise pre-select the recommended model for this stakeholder
          // type (FAIR Plan for homeowners); the user can still switch.
          if (autoSelectRecommended) {
            const recommended = list.find((c) => !c.isDefault && c.isRecommended);
            if (recommended) return recommended.id;
          }
          return undefined;
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    visible,
    stakeholderType,
    serviceAreaId,
    fetchChecklistsFor,
    defaultInsurer,
    autoSelectRecommended,
  ]);

  const insurerOptions = useMemo(() => checklists.filter((c) => !c.isDefault), [checklists]);
  // The fetched list includes the stakeholder type's isDefault doc (it's the
  // one insurerOptions filters out) — this is whatever "REDI Baseline" row
  // resolves to when selectedChecklistId is undefined.
  const defaultModel = useMemo(() => checklists.find((c) => c.isDefault), [checklists]);
  // Lead with the hazard of the model in play when the list loads: the
  // preselected one, else the default. Fixed per load, so picking a row in
  // another group doesn't reorder the list under the pointer.
  const [leadHazard, setLeadHazard] = useState<ModelHazard | undefined>(undefined);
  useEffect(() => {
    const current = selectedChecklistId
      ? checklists.find((c) => c.id === selectedChecklistId)
      : undefined;
    const lead = current ?? checklists.find((c) => c.isDefault);
    setLeadHazard(lead ? modelHazard(lead) : undefined);
    // Only a fresh list resets the lead; selection changes must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklists]);
  const modelGroups = useMemo(
    () => groupModelsByHazard(checklists, leadHazard),
    [checklists, leadHazard]
  );
  const defaultModelLabel =
    defaultModel?.displayName ?? defaultModel?.insurerName ?? 'REDI Baseline';
  const defaultModelDescription = defaultModel
    ? `${modelDescription(defaultModel)} · Default for this type. You can change models later.`
    : 'General wildfire prep. You can change models later.';

  return (
    <AdaptiveSheet
      isOpen={visible}
      variant="inline"
      snapPoints={['75%']}
      onRequestClose={onClose}
      onDismiss={onClose}
      dialogWidth={520}>
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <Text style={[styles.title, { color: colors.text }]}>{title}</Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          Pick who you are and a preparation model. Your checklist is generated from the model you
          choose, and you can change it later.
        </Text>
      </View>

      {/* BottomSheetScrollView (not plain ScrollView) so the sheet's pan
          gesture coordinates with the scroll on native; flex:1 keeps the
          footer pinned on-screen while the insurer list scrolls. */}
      <AdaptiveSheet.ScrollView
        testID="claim-sheet"
        style={styles.scroll}
        contentContainerStyle={styles.content}>
        <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>I am a</Text>
        <View style={styles.chipRow}>
          {STAKEHOLDER_OPTIONS.map((opt) => {
            const selected = opt.value === stakeholderType;
            const Icon = opt.icon;
            return (
              <Pressable
                key={opt.value}
                onPress={() => setStakeholderType(opt.value)}
                testID={`claim-stakeholder-${opt.value}`}
                accessibilityRole="radio"
                accessibilityState={{ selected }}
                // RN-web doesn't emit aria-* from accessibilityState; role=radio
                // requires aria-checked in the DOM (see OptionsPanel)
                aria-checked={selected}
                accessibilityLabel={`Stakeholder type: ${opt.label}`}
                style={[
                  styles.chip,
                  {
                    // Plaud grammar, same as OptionsPanel's visibility pills:
                    // hairline pill by default, ink pill when chosen. No
                    // brand tint.
                    borderColor: selected ? colors.action : colors.border,
                    backgroundColor: selected ? colors.action : 'transparent',
                  },
                ]}>
                <Icon size={16} color={selected ? colors.actionOn : colors.textSecondary} />
                <Text
                  style={[styles.chipLabel, { color: selected ? colors.actionOn : colors.text }]}>
                  {opt.label}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <Text style={[styles.sectionLabel, { color: colors.textSecondary, marginTop: SPACING.lg }]}>
          Preparation model
        </Text>

        {isLoadingList ? (
          <LoadingSpinner size="small" style={styles.loadingRow} color={colors.textSecondary} />
        ) : (
          <View style={styles.listContainer}>
            {!defaultModel && (
              <InsurerRow
                label={defaultModelLabel}
                description={defaultModelDescription}
                selected={selectedChecklistId === undefined}
                onPress={() => setSelectedChecklistId(undefined)}
              />
            )}
            {modelGroups.map((group) => (
              <View key={group.hazard}>
                <Text
                  testID={`claim-model-group-${group.hazard}`}
                  accessibilityRole="header"
                  style={[styles.groupHeader, { color: colors.textSecondary }]}>
                  {group.label}
                </Text>
                {group.models.map((c) => {
                  const label = c.displayName ?? c.insurerName;
                  if (c.isDefault) {
                    return (
                      <InsurerRow
                        key={c.id}
                        label={defaultModelLabel}
                        description={defaultModelDescription}
                        draftId={c.draft ? c.id : undefined}
                        selected={selectedChecklistId === undefined}
                        onPress={() => setSelectedChecklistId(undefined)}
                      />
                    );
                  }
                  return (
                    <InsurerRow
                      key={c.id}
                      label={label}
                      description={modelDescription(c)}
                      // Only surface the advocate when it adds info beyond the
                      // model name (agent-created models are named for their
                      // insurer, so this would just repeat the label).
                      sponsor={c.sponsor && c.sponsor !== label ? c.sponsor : undefined}
                      draftId={c.draft ? c.id : undefined}
                      selected={selectedChecklistId === c.id}
                      onPress={() => setSelectedChecklistId(c.id)}
                    />
                  );
                })}
              </View>
            ))}
            {insurerOptions.length === 0 && (
              <Text style={[styles.emptyNote, { color: colors.textTertiary }]}>
                No additional models are available for this type yet.
              </Text>
            )}
          </View>
        )}
        {/* Scroll sentinel: Maestro flows assert this is reachable. */}
        <View testID="claim-list-end" style={styles.scrollSentinel} />
      </AdaptiveSheet.ScrollView>

      <View style={[styles.footer, { borderTopColor: colors.border }]}>
        <ErrorBanner message={errorMessage} />
        <Button
          title={submitLabel}
          variant="primary"
          size="lg"
          fullWidth
          loading={isSubmitting}
          onPress={() => onSubmit({ stakeholderType, insurerChecklistId: selectedChecklistId })}
          testID="claim-submit"
        />
      </View>
    </AdaptiveSheet>
  );
}

function InsurerRow({
  label,
  description,
  sponsor,
  draftId,
  selected,
  onPress,
}: {
  label: string;
  description?: string;
  sponsor?: string;
  // Set on draft models; drives the "Draft" label and its testID.
  draftId?: string;
  selected: boolean;
  onPress: () => void;
}) {
  const { colors } = useTheme();
  const a11y = [
    label,
    description,
    sponsor ? `advocated by ${sponsor}` : null,
    draftId ? `draft. ${DRAFT_HINT}` : null,
  ]
    .filter(Boolean)
    .join(' — ');
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      // RN-web doesn't emit aria-* from accessibilityState (see OptionsPanel)
      aria-checked={selected}
      accessibilityLabel={a11y}
      style={[styles.row, { borderBottomColor: colors.border }]}>
      <View style={{ flex: 1 }}>
        <View style={styles.rowTitle}>
          <Text style={[styles.rowLabel, { color: colors.text }]}>{label}</Text>
          {draftId && (
            <Text
              testID={`claim-model-draft-${draftId}`}
              style={[
                styles.draftTag,
                { color: colors.textSecondary, borderColor: colors.border },
              ]}>
              Draft
            </Text>
          )}
        </View>
        {description && (
          <Text style={[styles.rowDescription, { color: colors.textSecondary }]}>
            {description}
          </Text>
        )}
        {draftId && (
          <Text style={[styles.rowSponsor, { color: colors.textSecondary }]}>{DRAFT_HINT}</Text>
        )}
        {sponsor && (
          <Text style={[styles.rowSponsor, { color: colors.textSecondary }]}>
            Advocated by {sponsor}
          </Text>
        )}
      </View>
      {selected && <Check size={18} color={colors.text} />}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  header: {
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  subtitle: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 4,
  },
  scroll: {
    flex: 1,
  },
  content: {
    padding: SPACING.md,
    paddingBottom: SPACING.xl,
  },
  scrollSentinel: {
    height: 1,
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.sm,
  },
  chipRow: {
    flexDirection: 'row',
    gap: SPACING.sm,
    flexWrap: 'wrap',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    gap: SPACING.sm,
  },
  chipLabel: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  // Hairline rows on canvas, not filled cards — the same shape the
  // appearance screen's radio list uses. Selection reads as the check, not
  // as a tinted box.
  // No top border: it would double with the section label's own spacing
  // (each row already carries its own bottom hairline).
  listContainer: {},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  groupHeader: {
    ...TYPOGRAPHY.overline,
    marginTop: SPACING.md,
    marginBottom: SPACING.xs,
  },
  rowTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: SPACING.sm,
  },
  draftTag: {
    ...TYPOGRAPHY.caption,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.full,
    paddingHorizontal: SPACING.sm,
  },
  rowLabel: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  rowDescription: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  rowSponsor: {
    ...TYPOGRAPHY.caption,
    marginTop: 2,
  },
  emptyNote: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.sm,
    fontStyle: 'italic',
  },
  loadingRow: {
    padding: SPACING.lg,
    alignItems: 'center',
  },
  footer: {
    padding: SPACING.md,
    gap: SPACING.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
});
